import React, { useEffect } from "react";
import { Link } from "react-router-dom";
/**
 * A modal to prompt users to upgrade their subscription.
 * @param {object} props - The component's props.
 * @param {boolean} props.isOpen - Controls if the modal is visible.
 * @param {function} props.onClose - Function to call when the modal should be closed.
 * @param {"proOrElite" | "elite"} [props.requiredTier="proOrElite"] - The subscription tier required for the feature.
 * @param {string} [props.upgradeUrl] - Optional override for the upgrade link URL.
 */
export default function UpgradeModal({
  isOpen,
  onClose,
  requiredTier = "proOrElite", 
  upgradeUrl,
}) {
  const isEliteOnly = requiredTier === "elite";

  // ✅ Hooks first, no conditional return before them
  useEffect(() => {
    if (!isOpen) return; // run only when the modal is open
    console.log(`[UpgradeModal] Opening with requiredTier: "${requiredTier}"`);
  }, [isOpen, requiredTier]);

  if (!isOpen) return null; // ✅ conditional return AFTER hooks

  // --- Dynamic Content ---
  // The text content changes based on the requiredTier prop.
  const headline = isEliteOnly
    ? "This Tool is for Elite Members"
    : "This Tool is for Professional & Elite Members";

  const subtext = isEliteOnly
    ? "Unlock Elite-only strategic lenses, deeper analysis, and premium accelerators designed for advanced operators."
    : "Unlock advanced strategic modes trusted by founders & executives to uncover hidden opportunities in minutes.";

  const ctaLabel = isEliteOnly ? "Upgrade to Elite" : "Upgrade & Unlock Now";
  
  const socialProof = isEliteOnly
    ? '“Elite members report faster insight-to-action by 2× within the first month.”'
    : '“90% of members say Professional paid for itself in the first week.”';

  // --- Dynamic URL ---
  // If a specific upgradeUrl isn't provided, we create one that can pre-select 
  // the correct plan on your upgrade page (e.g., /upgrade?tier=elite).
  const targetUrl =
    upgradeUrl ??
    (isEliteOnly ? "/upgrade?tier=elite" : "/upgrade?tier=pro");

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black bg-opacity-70 flex justify-center items-center animate-fadeIn" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-headline"
    >
      <div
        className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-lg shadow-2xl p-8 max-w-sm w-full mx-4 text-center transform transition-all animate-scaleIn"
        onClick={(e) => e.stopPropagation()} // Prevents modal from closing when clicking inside
      >
        <h2 className="text-3xl mb-3" aria-hidden="true">🔒</h2>

        <h3 id="upgrade-modal-headline" className="text-xl font-bold mb-3">
          {headline}
        </h3>

        <p className="text-white/80 mb-6">
          {subtext}
        </p>
        
        <Link
          to={targetUrl}
          className="w-full block bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-lg text-lg transition-transform duration-200 hover:scale-105"
        >
          {ctaLabel}
        </Link>

        <button
          onClick={onClose}
          className="w-full mt-4 text-white/50 hover:text-white/80 font-semibold"
        >
          Maybe Later
        </button>

        <div className="mt-6 pt-4 border-t border-white/10">
          <p className="text-xs text-white/60">
            {socialProof}
          </p>
        </div>

      </div>
    </div>
  );
}